import { axios } from '@ben/api/axios';
import { BaseAIAction } from '@ben/columbia/components/assistant/actions/base';
import {
  FollowUpType,
  InvalidArgumentError,
} from '@ben/columbia/components/assistant/types';

interface EligibilityRuleResponse {
  reasoning: string;
  query: string;
}

export class ExplainEligibilityRule extends BaseAIAction {
  constructor() {
    super();
    this.label = 'Explain Rule';
    this.requiresUserPrompt = false;
  }

  override async trigger(): Promise<[FollowUpType, unknown][]> {
    if (!this.args || this.args.length !== 1) {
      throw InvalidArgumentError;
    }

    const response = await axios.post<EligibilityRuleResponse>(
      'assistant/',
      {
        toolName: 'explainEligibilityRule',
        request: this.args[0],
      },
      {
        baseURL: `${process.env.REACT_APP_API_URL}api/v1.0/`,
      }
    );

    return [
      [FollowUpType.ChatMessage, response.data.reasoning],
      [FollowUpType.SetExecuted, []],
    ];
  }
}

export class CreateEligibilityRule extends BaseAIAction {
  constructor() {
    super();
    this.label = 'Create Rule';
    this.requiresUserPrompt = true;
    this.firstMessage = 'Please provide a prompt for the new eligibility rule';
  }

  override async trigger(): Promise<[FollowUpType, unknown][]> {
    if (!this.args || this.args.length !== 1) {
      throw InvalidArgumentError;
    }

    const response = await axios.post<EligibilityRuleResponse>(
      'assistant/',
      {
        toolName: 'createEligibilityRule',
        request: this.args[0],
      },
      {
        baseURL: `${process.env.REACT_APP_API_URL}api/v1.0/`,
      }
    );

    const modifyAgent = new ModifyEligibilityRule();
    modifyAgent.externalSetValue = this.externalSetValue;
    modifyAgent.args = [response.data.query];

    return [
      [
        FollowUpType.ChatMessage,
        [response.data.reasoning, response.data.query],
      ],
      [FollowUpType.UpdateInput, response.data.query],
      [FollowUpType.SwitchOption, modifyAgent],
    ];
  }
}

export class ModifyEligibilityRule extends BaseAIAction {
  constructor() {
    super();
    this.label = 'Modify Rule';
    this.requiresUserPrompt = true;
    this.firstMessage = 'What would you like to change?';
  }

  override async trigger(): Promise<[FollowUpType, unknown][]> {
    if (!this.args || this.args.length !== 2) {
      throw InvalidArgumentError;
    }

    const response = await axios.post<EligibilityRuleResponse>(
      'assistant/',
      {
        toolName: 'modifyEligibilityRule',
        request: this.args[1],
        query: this.args[0],
        previousChatMessages: this.chatMessages?.map((message) => {
          return {
            message:
              message.message +
              (message.background ? '\n' + message.background : ''),
            isUser: message.isUser,
          };
        }),
      },
      {
        baseURL: `${process.env.REACT_APP_API_URL}api/v1.0/`,
      }
    );

    return [
      [
        FollowUpType.ChatMessage,
        [response.data.reasoning, response.data.query],
      ],
      [FollowUpType.UpdateInput, response.data.query],
      [FollowUpType.ContinueConversation, [response.data.query]],
    ];
  }
}
